<template>
  <div
    :class="[
      'fixed z-[10000] min-w-[320px] max-w-md',
      'bg-surface border rounded-xl shadow-2xl overflow-hidden'
    ]"
    :style="toastStyle"
  >
    <div class="flex items-start gap-3 p-4 pb-3">
      <!-- Icon -->
      <div
        :class="[
          'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
          iconBgClass
        ]"
      >
        <component :is="iconComponent" :class="['w-5 h-5', iconColorClass]" />
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0 pt-1">
        <h4 class="text-sm font-semibold text-text-main mb-1">
          {{ title }}
        </h4>
        <p v-if="message" class="text-sm text-text-muted">
          {{ message }}
        </p>
      </div>

      <!-- Close Button -->
      <button
        @click="close"
        class="w-8 h-8 rounded-lg hover:bg-hover flex items-center justify-center transition-colors shrink-0"
      >
        <X class="w-4 h-4 text-text-muted" />
      </button>
    </div>

    <!-- Progress Bar -->
    <div
      v-if="duration > 0"
      class="px-4 pb-3"
    >
      <div class="h-0.5 bg-background rounded-full overflow-hidden w-[85%]">
        <div
          class="h-full transition-all ease-linear rounded-full"
          :class="progressBarClass"
          :style="{ width: `${progress}%` }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-vue-next'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Props {
  type?: ToastType
  title: string
  message?: string
  duration?: number // 0 means no auto close
  index?: number // For stacking toasts
}

interface Emits {
  (e: 'close'): void
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  duration: 3000,
  index: 0
})

const emit = defineEmits<Emits>()

const progress = ref(100)
let progressTimer: NodeJS.Timeout | null = null

// Calculate position based on index for stacking
const toastStyle = computed(() => {
  const topOffset = 16 + (props.index * 88) // 16px initial + 88px per toast
  return {
    top: `${topOffset}px`,
    right: '16px'
  }
})

const iconComponent = computed(() => {
  const icons = {
    success: CheckCircle2,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info
  }
  return icons[props.type]
})

const iconBgClass = computed(() => {
  const classes = {
    success: 'bg-green-500/10',
    error: 'bg-red-500/10',
    warning: 'bg-yellow-500/10',
    info: 'bg-blue-500/10'
  }
  return classes[props.type]
})

const iconColorClass = computed(() => {
  const classes = {
    success: 'text-green-500',
    error: 'text-red-500',
    warning: 'text-yellow-500',
    info: 'text-blue-500'
  }
  return classes[props.type]
})

const progressBarClass = computed(() => {
  const classes = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  }
  return classes[props.type]
})

const close = () => {
  emit('close')
  clearTimers()
}

const clearTimers = () => {
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
}

const startAutoClose = () => {
  if (props.duration > 0) {
    progress.value = 100
    
    // Progress bar animation
    const interval = 50
    const steps = props.duration / interval
    const decrement = 100 / steps
    
    progressTimer = setInterval(() => {
      progress.value -= decrement
      if (progress.value <= 0) {
        progress.value = 0
        clearTimers()
      }
    }, interval)
  }
}

onMounted(() => {
  startAutoClose()
})
</script>

<style scoped>
/* Styles are now handled by parent TransitionGroup */
</style>
